import React, { useState, useRef, useCallback } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Link } from 'react-router-dom';
import SEOHead from '../components/SEOHead';
import {
  ArrowRight,
  Phone,
  Target,
  BookOpen,
  ChevronDown,
  ChevronUp,
  Clock
} from 'lucide-react';

const Wissen = () => {
  const [expandedArticle, setExpandedArticle] = useState(null);

  const seoJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: 'Wissen & Tipps für KMU – IT-Ratgeber Mönchengladbach',
    description: 'Praktische IT-Tipps für KMU: Microsoft 365, Digitalisierung, IT-Sicherheit. Verständlich erklärt, ohne Fachjargon.',
    url: 'https://schteffann.de/wissen'
  };

  const artikel = [
    {
      id: 1,
      titel: 'Microsoft 365: Die 5 wichtigsten Tools für KMU',
      kategorie: 'Microsoft 365',
      lesedauer: '5 min',
      vorschau: 'Sie zahlen für Microsoft 365, nutzen aber nur Outlook? Hier sind die fünf Tools, die Ihnen wirklich Zeit sparen.',
      inhalt: `<div class="prose max-w-none">
        <p>Viele Unternehmen haben Microsoft 365 – aber die meisten nutzen nur einen Bruchteil davon. Dabei stecken in dem Paket Tools, die echten Mehrwert bringen. Hier sind die fünf wichtigsten:</p>
        
        <h3>1. Teams – Weniger E-Mails, mehr Überblick</h3>
        <p>Teams ersetzt nicht nur E-Mails, sondern bringt Kommunikation und Dateien an einen Ort. Sie können Channels für Projekte oder Themen anlegen, Dateien direkt im Chat teilen und Videocalls starten. Das spart Zeit und verhindert E-Mail-Chaos.</p>
        
        <h3>2. SharePoint – Zentrale Dateiablage</h3>
        <p>Statt Dateien auf verschiedenen Festplatten oder in E-Mail-Anhängen: SharePoint ist eine zentrale Ablage, auf die alle zugreifen können. Mit Versionierung, klaren Berechtigungen und automatischer Synchronisierung.</p>
        
        <h3>3. OneDrive – Persönlicher Speicher, überall verfügbar</h3>
        <p>Ihr persönlicher Cloud-Speicher. Automatische Sicherung, Zugriff von überall, kein USB-Stick mehr nötig. Perfekt für Dokumente, die nur Sie brauchen.</p>
        
        <h3>4. Planner – Aufgaben im Blick</h3>
        <p>Wer macht was bis wann? Planner ist ein einfaches Aufgaben-Tool: Boards erstellen, Aufgaben zuweisen, Status verfolgen. Ohne Schnickschnack, direkt nutzbar.</p>
        
        <h3>5. Forms – Formulare und Umfragen</h3>
        <p>Brauchen Sie Rückmeldungen von Kunden oder Mitarbeitern? Forms erstellt schnell Formulare, die Ergebnisse landen automatisch in Excel. Einfacher geht's nicht.</p>
        
        <h3>Fazit</h3>
        <p>Sie müssen nicht alles auf einmal nutzen. Starten Sie mit einem Tool und schauen Sie, was es bringt. Meist reicht das schon, um spürbar Zeit zu sparen.</p>
      </div>`
    },
    {
      id: 2,
      titel: 'Backups richtig machen: Was KMU wissen müssen',
      kategorie: 'IT-Sicherheit',
      lesedauer: '4 min',
      vorschau: 'Backups hat jeder. Aber funktionieren sie auch? Die wichtigsten Regeln für KMU.',
      inhalt: `<div class="prose max-w-none">
        <p>Backups sind wie eine Versicherung: Man braucht sie hoffentlich nie, aber wenn doch, ist man froh, sie zu haben. Trotzdem machen viele KMU gravierende Fehler.</p>
        
        <h3>Die 3-2-1-Regel</h3>
        <p>Eine einfache Faustregel: 3 Kopien Ihrer Daten, auf 2 verschiedenen Medien, 1 davon extern. Beispiel: Original auf dem Server, Backup auf externer Festplatte, zweites Backup in der Cloud.</p>
        
        <h3>Häufigste Fehler</h3>
        <ul>
          <li><strong>Nie getestet:</strong> Viele Backups laufen automatisch, aber niemand prüft, ob die Wiederherstellung funktioniert.</li>
          <li><strong>Zu selten:</strong> Einmal pro Monat reicht nicht. Täglich ist besser, bei kritischen Daten mehrmals täglich.</li>
          <li><strong>Am selben Ort:</strong> Backup und Original im gleichen Büro? Bei Brand oder Einbruch sind beide weg.</li>
          <li><strong>Keine Verschlüsselung:</strong> Backups enthalten sensible Daten und müssen verschlüsselt sein.</li>
        </ul>
        
        <h3>Was Sie tun sollten</h3>
        <p>1. Automatische Backups einrichten (täglich oder mehrmals täglich)<br/>
        2. Mindestens einmal pro Quartal einen Restore-Test machen<br/>
        3. Eine Kopie extern lagern (Cloud oder externe Festplatte an anderem Ort)<br/>
        4. Dokumentieren, was gesichert wird und wie man wiederherstellt</p>
        
        <h3>Cloud-Backups: Ja oder nein?</h3>
        <p>Cloud-Backups sind praktisch und sicher – wenn sie verschlüsselt sind. Microsoft 365 bietet automatische Sicherung, reicht aber allein nicht. Eine zusätzliche Backup-Lösung ist Pflicht.</p>
        
        <h3>Fazit</h3>
        <p>Backups sind kein Hexenwerk. Aber sie müssen richtig eingerichtet und regelmäßig getestet werden. Sonst sind sie im Ernstfall nutzlos.</p>
      </div>`
    },
    {
      id: 3,
      titel: 'Passwörter: So machen Sie es richtig (ohne Stress)',
      kategorie: 'IT-Sicherheit',
      lesedauer: '3 min',
      vorschau: 'Sichere Passwörter müssen nicht kompliziert sein. Ein Passwortmanager hilft – und ist einfacher als Sie denken.',
      inhalt: `<div class="prose max-w-none">
        <p>Hand aufs Herz: Wie viele Ihrer Passwörter sind "12345", "Firmenname2023" oder auf einem Zettel notiert? Sie sind nicht allein. Aber es gibt eine bessere Lösung.</p>
        
        <h3>Das Problem mit Passwörtern</h3>
        <p>Sichere Passwörter sollten lang, einzigartig und zufällig sein. Aber wer kann sich "X9!mK2$pL7@Qr4" merken? Niemand. Deshalb nutzen die meisten Menschen einfache Passwörter – oder immer dasselbe.</p>
        
        <h3>Die Lösung: Passwortmanager</h3>
        <p>Ein Passwortmanager speichert alle Ihre Passwörter verschlüsselt. Sie müssen sich nur noch ein einziges Master-Passwort merken. Der Rest läuft automatisch.</p>
        
        <h3>Wie funktioniert das?</h3>
        <p>1. Sie installieren einen Passwortmanager (z.B. Bitwarden, 1Password, KeePass)<br/>
        2. Sie legen ein sicheres Master-Passwort fest (z.B. ein Satz: "IchTrinke3TassenKaffee!")<br/>
        3. Für jede Website generiert der Manager ein sicheres, einzigartiges Passwort<br/>
        4. Beim nächsten Login füllt er es automatisch aus</p>
        
        <h3>Ist das sicher?</h3>
        <p>Ja. Sicherer als Zettel, Excel-Listen oder immer dasselbe Passwort. Der Manager verschlüsselt alles, selbst der Anbieter kann Ihre Passwörter nicht lesen.</p>
        
        <h3>Was ist mit Zwei-Faktor-Authentifizierung?</h3>
        <p>Die sollten Sie zusätzlich aktivieren, wo immer möglich. Ein zweiter Sicherheitsschritt (z.B. Code per SMS oder App) verhindert 99% aller Angriffe.</p>
        
        <h3>Fazit</h3>
        <p>Ein Passwortmanager ist die einfachste Maßnahme für mehr Sicherheit. Einmal eingerichtet, macht er Ihr Leben einfacher – nicht komplizierter.</p>
      </div>`
    },
    {
      id: 4,
      titel: 'Teams vs. E-Mail: Wann nutze ich was?',
      kategorie: 'Microsoft 365',
      lesedauer: '4 min',
      vorschau: 'E-Mail oder Teams? Beide haben ihre Berechtigung. Hier ist, wann Sie was nutzen sollten.',
      inhalt: `<div class="prose max-w-none">
        <p>Seit Microsoft Teams gibt es eine neue Frage: Soll ich eine E-Mail schreiben oder in Teams posten? Die Antwort ist: Es kommt drauf an.</p>
        
        <h3>E-Mail ist gut für:</h3>
        <ul>
          <li>Formelle Kommunikation nach außen (Kunden, Partner)</li>
          <li>Wichtige Dokumente, die archiviert werden müssen</li>
          <li>Kommunikation mit Personen außerhalb Ihres Teams</li>
          <li>Wenn eine Bestätigung wichtig ist (rechtlich relevant)</li>
        </ul>
        
        <h3>Teams ist besser für:</h3>
        <ul>
          <li>Schnelle Absprachen im Team</li>
          <li>Projektbezogene Kommunikation</li>
          <li>Dateien, die gemeinsam bearbeitet werden</li>
          <li>Alles, was "live" besprochen werden muss</li>
        </ul>
        
        <h3>Typische Szenarien</h3>
        <p><strong>Szenario 1: Kundenanfrage</strong><br/>
        → E-Mail. Formell, nachverfolgbar, nach außen.</p>
        
        <p><strong>Szenario 2: Teamabstimmung zu einem Projekt</strong><br/>
        → Teams. Schnell, alle Infos an einem Ort, keine E-Mail-Flut.</p>
        
        <p><strong>Szenario 3: Angebot verschicken</strong><br/>
        → E-Mail. Dokumentiert, formell, rechtlich relevant.</p>
        
        <p><strong>Szenario 4: "Wer kann heute Nachmittag?"</strong><br/>
        → Teams. Schnelle Rückmeldungen, nicht wichtig genug für E-Mail.</p>
        
        <h3>Häufige Fehler</h3>
        <p>1. Alles über E-Mail → E-Mail-Flut<br/>
        2. Alles über Teams → Wichtige Sachen gehen unter<br/>
        3. Hin und her zwischen beiden → Chaos</p>
        
        <h3>Fazit</h3>
        <p>Legen Sie im Team Regeln fest: Was läuft über Teams, was über E-Mail? Dann hält sich jeder daran. Problem gelöst.</p>
      </div>`
    },
    {
      id: 5,
      titel: 'Digitalisierung für KMU: Wo anfangen?',
      kategorie: 'Digitalisierung',
      lesedauer: '6 min',
      vorschau: 'Digitalisierung klingt groß. Ist es aber nicht. Hier sind drei einfache Startpunkte für KMU.',
      inhalt: `<div class="prose max-w-none">
        <p>Digitalisierung ist ein Buzzword, das viele abschreckt. Dabei geht es nur darum: Arbeitsabläufe einfacher machen. Nicht mehr, nicht weniger.</p>
        
        <h3>Schritt 1: Zeitfresser finden</h3>
        <p>Schauen Sie sich Ihren Alltag an: Wo verschwenden Sie Zeit? Was nervt? Häufige Kandidaten:</p>
        <ul>
          <li>Angebote erstellen</li>
          <li>Rechnungen freigeben</li>
          <li>Urlaubsanträge</li>
          <li>Dateien suchen</li>
          <li>Informationen weitergeben</li>
        </ul>
        
        <h3>Schritt 2: Einen Prozess auswählen</h3>
        <p>Nicht alles auf einmal. Suchen Sie sich einen Prozess aus, der Sie am meisten nervt. Beispiel: Angebotserstellung.</p>
        
        <p><strong>Vorher:</strong> Word-Vorlage suchen, Daten manuell eintragen, Preise nachschlagen, als PDF speichern, per E-Mail verschicken.</p>
        
        <p><strong>Nachher:</strong> Formular ausfüllen (Microsoft Forms oder ähnliches), automatisch Angebot erstellen lassen, per Klick verschicken.</p>
        
        <h3>Schritt 3: Einfach starten</h3>
        <p>Sie brauchen keine teure Software. Oft reicht Microsoft 365:</p>
        <ul>
          <li>Forms für Formulare</li>
          <li>Power Automate für automatische Abläufe</li>
          <li>SharePoint für zentrale Ablage</li>
          <li>Teams für Kommunikation</li>
        </ul>
        
        <h3>Typische Fehler</h3>
        <p>1. Zu viel auf einmal wollen<br/>
        2. Komplizierte Tools kaufen, die niemand versteht<br/>
        3. Das Team nicht mitnehmen<br/>
        4. Keine Zeit für Einarbeitung einplanen</p>
        
        <h3>Realistischer Zeitplan</h3>
        <p>Woche 1: Prozess aussuchen, ist-Situation aufschreiben<br/>
        Woche 2: Lösung bauen oder einrichten<br/>
        Woche 3: Team schulen, testen<br/>
        Woche 4: Live gehen, anpassen</p>
        
        <h3>Fazit</h3>
        <p>Digitalisierung heißt nicht, alles umzukrempeln. Starten Sie klein, sehen Sie Erfolge, machen Sie weiter. Ein Prozess nach dem anderen.</p>
      </div>`
    },
    {
      id: 6,
      titel: 'DSGVO für KMU: Was Sie wirklich tun müssen',
      kategorie: 'IT-Sicherheit',
      lesedauer: '5 min',
      vorschau: 'DSGVO klingt kompliziert. Die Basics sind aber machbar – und Pflicht.',
      inhalt: `<div class="prose max-w-none">
        <p>Die DSGVO (Datenschutz-Grundverordnung) gilt seit 2018. Trotzdem sind viele KMU unsicher: Was muss ich wirklich tun? Hier sind die wichtigsten Punkte, verständlich erklärt.</p>
        
        <h3>1. Datenschutzerklärung auf der Website</h3>
        <p>Pflicht. Muss erklären, welche Daten Sie sammeln (z.B. über Kontaktformulare, Cookies) und was Sie damit machen. Gibt es als Generator online, sollte aber geprüft werden.</p>
        
        <h3>2. Verzeichnis der Verarbeitungstätigkeiten</h3>
        <p>Klingt kompliziert, ist aber nur eine Liste: Wo speichern Sie welche Daten? Beispiele: Kundendaten in Excel, E-Mails in Outlook, Rechnungen in DATEV. Einmal aufschreiben, fertig.</p>
        
        <h3>3. Auftragsverarbeitungsverträge (AVV)</h3>
        <p>Wenn ein Dienstleister Ihre Daten verarbeitet (z.B. Cloud-Anbieter, Steuerberater), brauchen Sie einen AVV. Die meisten Anbieter haben Vorlagen, einfach unterschreiben.</p>
        
        <h3>4. Technische Maßnahmen</h3>
        <p>Ihre Daten müssen geschützt sein:</p>
        <ul>
          <li>Verschlüsselte Verbindungen (HTTPS auf Website, SSL in E-Mails)</li>
          <li>Sichere Passwörter</li>
          <li>Regelmäßige Backups</li>
          <li>Zugriffsrechte: Nicht jeder darf alles sehen</li>
        </ul>
        
        <h3>5. Löschkonzept</h3>
        <p>Daten, die Sie nicht mehr brauchen, müssen gelöscht werden. Legen Sie Fristen fest: z.B. Kundendaten 10 Jahre nach letztem Kontakt, Bewerbungen 6 Monate nach Absage.</p>
        
        <h3>Brauche ich einen Datenschutzbeauftragten?</h3>
        <p>In den meisten KMU: Nein. Nur wenn Sie mehr als 20 Mitarbeiter haben, die ständig mit personenbezogenen Daten arbeiten. Im Zweifel: Kurz prüfen lassen.</p>
        
        <h3>Was passiert bei Verstößen?</h3>
        <p>Bußgelder sind möglich, aber selten bei KMU. Wichtiger: Wenn etwas passiert (z.B. Datenpanne), müssen Sie es melden. Deshalb lieber vorher die Basics erledigen.</p>
        
        <h3>Fazit</h3>
        <p>DSGVO ist machbar. Die meisten Maßnahmen sind einmalig und mit etwas Unterstützung schnell erledigt. Danach läuft es nebenbei.</p>
      </div>`
    },
    {
      id: 7,
      titel: 'Cloud oder Server? Was für KMU sinnvoll ist',
      kategorie: 'IT-Infrastruktur',
      lesedauer: '5 min',
      vorschau: 'Eigener Server oder Cloud? Die Vor- und Nachteile für kleine Unternehmen.',
      inhalt: `<div class="prose max-w-none">
        <p>Die Frage stellt sich früher oder später: Eigener Server im Büro oder alles in die Cloud? Die Antwort: Kommt drauf an.</p>
        
        <h3>Option 1: Lokaler Server</h3>
        <p><strong>Vorteile:</strong></p>
        <ul>
          <li>Volle Kontrolle über Ihre Daten</li>
          <li>Keine Abhängigkeit vom Internet</li>
          <li>Einmalige Anschaffungskosten</li>
        </ul>
        
        <p><strong>Nachteile:</strong></p>
        <ul>
          <li>Wartung, Updates, Backups müssen Sie (oder ein Dienstleister) machen</li>
          <li>Kein Zugriff von außen (Homeoffice schwierig)</li>
          <li>Stromkosten, Platz, Lärm</li>
          <li>Bei Ausfall steht alles</li>
        </ul>
        
        <h3>Option 2: Cloud</h3>
        <p><strong>Vorteile:</strong></p>
        <ul>
          <li>Von überall Zugriff</li>
          <li>Automatische Updates und Backups</li>
          <li>Skalierbar: Mehr Speicher? Einfach hinzubuchen</li>
          <li>Keine Hardware-Wartung</li>
        </ul>
        
        <p><strong>Nachteile:</strong></p>
        <ul>
          <li>Monatliche Kosten</li>
          <li>Abhängigkeit vom Internet</li>
          <li>Daten liegen bei einem Anbieter (muss vertrauenswürdig sein)</li>
        </ul>
        
        <h3>Option 3: Hybrid (beides kombinieren)</h3>
        <p>Viele KMU nutzen eine Mischung: Sensible Daten lokal, Collaboration-Tools in der Cloud. Beispiel: Buchhaltung auf lokalem Server, Microsoft 365 in der Cloud.</p>
        
        <h3>Für wen ist was geeignet?</h3>
        <p><strong>Lokaler Server passt, wenn:</strong></p>
        <ul>
          <li>Sie spezielle Software nutzen, die nur lokal läuft</li>
          <li>Sie sehr große Dateien haben (z.B. CAD, Video)</li>
          <li>Sie aus rechtlichen Gründen lokale Speicherung brauchen</li>
        </ul>
        
        <p><strong>Cloud passt, wenn:</strong></p>
        <ul>
          <li>Sie flexibel arbeiten wollen (Homeoffice, unterwegs)</li>
          <li>Sie keine IT-Abteilung haben</li>
          <li>Sie automatische Sicherheit wollen (Updates, Backups)</li>
        </ul>
        
        <h3>Fazit</h3>
        <p>Die meisten KMU fahren heute gut mit Cloud-Lösungen. Spezialsoftware läuft lokal, der Rest in der Cloud. Das gibt Flexibilität ohne großen Wartungsaufwand.</p>
      </div>`
    },
    {
      id: 8,
      titel: 'SharePoint-Ordnerstruktur: So machen Sie es richtig',
      kategorie: 'Microsoft 365',
      lesedauer: '4 min',
      vorschau: 'Ordnerchaos in SharePoint? Mit diesen Regeln schaffen Sie Struktur.',
      inhalt: `<div class="prose max-w-none">
        <p>SharePoint kann großartig sein – oder ein hoffnungsloses Durcheinander. Der Unterschied liegt in der Struktur. Hier ist, wie Sie es richtig machen.</p>
        
        <h3>Grundregel: So einfach wie möglich</h3>
        <p>Je komplizierter die Struktur, desto weniger wird sie genutzt. Maximal 3-4 Ebenen tief, klare Namen, logische Ordnung.</p>
        
        <h3>Variante 1: Nach Abteilungen</h3>
        <p>Einfach und übersichtlich:</p>
        <ul>
          <li>Geschäftsführung</li>
          <li>Vertrieb</li>
          <li>Buchhaltung</li>
          <li>Personal</li>
        </ul>
        <p>Passt für kleine Unternehmen mit klaren Abteilungen.</p>
        
        <h3>Variante 2: Nach Projekten</h3>
        <p>Für projektbasierte Arbeit:</p>
        <ul>
          <li>Projekt A</li>
          <li>Projekt B</li>
          <li>Projekt C</li>
        </ul>
        <p>Jedes Projekt hat die gleichen Unterordner (z.B. Planung, Umsetzung, Abrechnung).</p>
        
        <h3>Variante 3: Nach Kunden</h3>
        <p>Für Dienstleister:</p>
        <ul>
          <li>Kunde 1</li>
          <li>Kunde 2</li>
          <li>Kunde 3</li>
        </ul>
        <p>Pro Kunde die gleiche Unterstruktur (Verträge, Korrespondenz, Projekte).</p>
        
        <h3>Wichtige Regeln</h3>
        <p>1. <strong>Einheitliche Benennung:</strong> Legen Sie fest, wie Ordner heißen sollen. "Kunde_Name_Jahr" ist besser als mal "Name" und mal "Firma".<br/>
        2. <strong>Keine Duplikate:</strong> Ein Dokument gehört an einen Ort. Nicht kopieren.<br/>
        3. <strong>Versionierung nutzen:</strong> SharePoint speichert alte Versionen automatisch. Keine "_final_v2_neu" mehr.<br/>
        4. <strong>Zugriffsrechte klar regeln:</strong> Wer darf was sehen? Einmal einrichten, dann läuft es.</p>
        
        <h3>Häufige Fehler</h3>
        <ul>
          <li>Zu viele Unterordner (5+ Ebenen) → Niemand findet mehr was</li>
          <li>Kryptische Namen ("Ordner 2023 neu") → Verwirrung</li>
          <li>Keine Struktur → Jeder legt an, wo er will</li>
        </ul>
        
        <h3>Fazit</h3>
        <p>Nehmen Sie sich eine Stunde Zeit, um die Grundstruktur festzulegen. Schulen Sie Ihr Team. Halten Sie sich daran. Das spart Ihnen Stunden pro Woche beim Suchen.</p>
      </div>`
    },
    {
      id: 9,
      titel: 'IT-Notfallplan für KMU: Was Sie vorbereiten sollten',
      kategorie: 'IT-Sicherheit',
      lesedauer: '5 min',
      vorschau: 'Was tun, wenn die IT ausfällt? Ein einfacher Notfallplan hilft.',
      inhalt: `<div class="prose max-w-none">
        <p>Server ausgefallen, Daten weg, niemand weiß, was zu tun ist. Ein IT-Notfall kann jeden treffen. Mit einem einfachen Plan sind Sie vorbereitet.</p>
        
        <h3>Was ist ein IT-Notfall?</h3>
        <p>Alles, was Ihre Arbeit stoppt:</p>
        <ul>
          <li>Server-Ausfall</li>
          <li>Ransomware (Erpressungs-Virus)</li>
          <li>Datenverlust</li>
          <li>Internetausfall</li>
          <li>Microsoft 365 fällt aus</li>
        </ul>
        
        <h3>Was gehört in einen Notfallplan?</h3>
        
        <p><strong>1. Kontaktliste</strong></p>
        <p>Wer hilft im Notfall?</p>
        <ul>
          <li>IT-Dienstleister (Telefonnummer, E-Mail)</li>
          <li>Hosting-Anbieter</li>
          <li>Microsoft-Support</li>
          <li>Intern: Wer darf Entscheidungen treffen?</li>
        </ul>
        
        <p><strong>2. Backup-Informationen</strong></p>
        <p>Wo sind Backups? Wie stellt man sie wieder her? Schritt-für-Schritt-Anleitung, auch für Nicht-IT-Profis.</p>
        
        <p><strong>3. Kritische Systeme</strong></p>
        <p>Was muss sofort laufen? Prioritäten setzen:</p>
        <ul>
          <li>Prio 1: E-Mail, Kundendaten</li>
          <li>Prio 2: Buchhaltung, Dateiablage</li>
          <li>Prio 3: Alles andere</li>
        </ul>
        
        <p><strong>4. Zugangsdaten</strong></p>
        <p>Wo sind wichtige Zugangsdaten gespeichert? (Verschlüsselt, an sicherem Ort, nur für berechtigte Personen)</p>
        
        <p><strong>5. Kommunikationsplan</strong></p>
        <p>Wer informiert wen? Kunden? Mitarbeiter? Wie kommunizieren Sie, wenn E-Mail ausfällt?</p>
        
        <h3>Notfallplan testen</h3>
        <p>Ein Plan, der nicht getestet wurde, funktioniert im Ernstfall nicht. Machen Sie einmal im Jahr einen Probe-Restore Ihrer Backups.</p>
        
        <h3>Wo aufbewahren?</h3>
        <p>Der Notfallplan muss auch verfügbar sein, wenn die IT ausfällt. Gedruckt im Safe, verschlüsselt in der Cloud, bei mehreren Personen.</p>
        
        <h3>Fazit</h3>
        <p>Ein IT-Notfallplan ist kein Hexenwerk. Ein paar Stunden Arbeit können im Ernstfall Tage oder Wochen Stillstand verhindern.</p>
      </div>`
    },
    {
      id: 10,
      titel: 'Homeoffice-IT: Was KMU beachten sollten',
      kategorie: 'IT-Infrastruktur',
      lesedauer: '4 min',
      vorschau: 'Homeoffice ist Alltag geworden. Diese IT-Basics sollten Sie klären.',
      inhalt: `<div class="prose max-w-none">
        <p>Homeoffice ist für viele KMU normal geworden. Aber die IT muss dafür richtig aufgestellt sein. Hier sind die wichtigsten Punkte.</p>
        
        <h3>1. Zugriff auf Firmendaten</h3>
        <p>Wie kommen Mitarbeiter von zu Hause an Dateien?</p>
        <ul>
          <li><strong>Cloud-Lösung:</strong> Microsoft 365, OneDrive, SharePoint – von überall erreichbar</li>
          <li><strong>VPN:</strong> Sichere Verbindung zum Firmen-Server (wenn lokaler Server nötig ist)</li>
          <li><strong>Remote Desktop:</strong> Auf den Büro-PC zugreifen (nur wenn wirklich nötig)</li>
        </ul>
        
        <h3>2. Kommunikation</h3>
        <p>Wie bleibt das Team in Kontakt?</p>
        <ul>
          <li>Microsoft Teams oder ähnliches für Chat und Calls</li>
          <li>Klare Regeln: Wann ist wer erreichbar?</li>
          <li>Tägliche oder wöchentliche Team-Calls</li>
        </ul>
        
        <h3>3. Hardware</h3>
        <p>Was brauchen Mitarbeiter zu Hause?</p>
        <ul>
          <li>Laptop oder privater PC? (Klären Sie das arbeitsrechtlich)</li>
          <li>Bildschirm, Tastatur, Maus (ergonomisch wichtig)</li>
          <li>Headset für Calls</li>
          <li>Drucker? (Oft nicht nötig, wenn digital gearbeitet wird)</li>
        </ul>
        
        <h3>4. Sicherheit</h3>
        <p>Homeoffice ist ein Sicherheitsrisiko. Das müssen Sie beachten:</p>
        <ul>
          <li>VPN-Verbindung verschlüsselt</li>
          <li>Keine Firmendaten auf privaten Geräten (oder nur verschlüsselt)</li>
          <li>Sichere Passwörter + Zwei-Faktor-Authentifizierung</li>
          <li>Regelmäßige Updates auf allen Geräten</li>
        </ul>
        
        <h3>5. Regeln festlegen</h3>
        <p>Was ist erlaubt, was nicht?</p>
        <ul>
          <li>Dürfen Mitarbeiter auf öffentlichem WLAN arbeiten? (Besser nicht ohne VPN)</li>
          <li>Wo dürfen Firmendaten gespeichert werden?</li>
          <li>Was passiert bei Geräte-Verlust?</li>
        </ul>
        
        <h3>Homeoffice-Vereinbarung</h3>
        <p>Halten Sie die Regeln schriftlich fest. Nicht kompliziert, aber klar. Das schützt beide Seiten.</p>
        
        <h3>Fazit</h3>
        <p>Homeoffice funktioniert nur mit der richtigen IT-Basis. Cloud-Zugriff, Kommunikations-Tools, Sicherheitsregeln. Einmal aufgesetzt, läuft es dann aber.</p>
      </div>`
    }
  ];

  const cardRefs = useRef({});

  const toggleArticle = useCallback((id) => {
    const isOpening = expandedArticle !== id;
    setExpandedArticle(isOpening ? id : null);

    if (isOpening) {
      requestAnimationFrame(() => {
        const el = cardRefs.current[id];
        if (el) {
          const headerOffset = 100;
          const top = el.getBoundingClientRect().top + window.scrollY - headerOffset;
          window.scrollTo({ top, behavior: 'smooth' });
        }
      });
    }
  }, [expandedArticle]);

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <SEOHead
        title="Wissen & Tipps für KMU – IT-Ratgeber Mönchengladbach | Schteffann"
        description="Praktische IT-Tipps für KMU: Microsoft 365, Digitalisierung, IT-Sicherheit. Verständlich erklärt, ohne Fachjargon. Von Stephan Schunter, IT-Experte in Mönchengladbach."
        keywords="IT Tipps KMU, Microsoft 365 Ratgeber, IT Sicherheit Tipps, Digitalisierung KMU, Backups KMU, Mönchengladbach IT, Schteffann"
        jsonLd={seoJsonLd}
      />
      {/* Hero */}
      <section className="relative py-20 lg:py-24 overflow-hidden bg-gradient-to-br from-cyan-50/50 via-blue-50/30 to-slate-50">
        <div className="absolute inset-0 bg-gradient-to-r from-cyan-500/5 to-blue-600/5"></div>
        <div className="container relative">
          <div className="max-w-4xl mx-auto text-center">
            <Badge className="mb-6 bg-gradient-to-r from-cyan-100 to-blue-100 text-cyan-800 border-cyan-200 px-6 py-2">
              <BookOpen className="w-4 h-4 mr-2" />
              Wissen
            </Badge>
            <h1 className="text-4xl lg:text-6xl font-bold mb-6 leading-tight">
              <span className="text-slate-900">Praktische IT-Tipps </span>
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-600 via-blue-600 to-cyan-700">für KMU</span>
            </h1>
            <p className="text-xl lg:text-2xl text-slate-600 leading-relaxed">
              Verständlich erklärt, ohne Fachjargon. Tipps, die Sie wirklich umsetzen können.
            </p>
          </div>
        </div>
      </section>

      {/* Artikel */}
      <section className="py-20">
        <div className="container">
          <div className="max-w-4xl mx-auto space-y-6">
            {artikel.map((article) => (
              <Card key={article.id} ref={(el) => (cardRefs.current[article.id] = el)} className="border-slate-200 shadow-sm hover:shadow-md transition-shadow">
                <CardContent className="p-0">
                  <button
                    onClick={() => toggleArticle(article.id)}
                    className="w-full p-6 lg:p-8 text-left hover:bg-slate-50 transition-colors"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-3">
                          <Badge className="bg-cyan-100 text-cyan-800 border-cyan-200">
                            {article.kategorie}
                          </Badge>
                          <div className="flex items-center text-sm text-slate-500">
                            <Clock className="h-4 w-4 mr-1" />
                            {article.lesedauer}
                          </div>
                        </div>
                        <h2 className="text-2xl font-bold text-slate-900 mb-3 group-hover:text-cyan-700 transition-colors">
                          {article.titel}
                        </h2>
                        <p className="text-slate-600 leading-relaxed">
                          {article.vorschau}
                        </p>
                      </div>
                      <div className="ml-4 flex-shrink-0">
                        {expandedArticle === article.id ? (
                          <ChevronUp className="h-6 w-6 text-cyan-600" />
                        ) : (
                          <ChevronDown className="h-6 w-6 text-slate-400" />
                        )}
                      </div>
                    </div>
                  </button>
                  
                  {expandedArticle === article.id && (
                    <div className="px-6 lg:px-8 pb-8 border-t border-slate-200 pt-6">
                      <div 
                        className="prose prose-slate max-w-none"
                        dangerouslySetInnerHTML={{ __html: article.inhalt }}
                      />
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20">
        <div className="container">
          <Card className="bg-gradient-to-br from-cyan-600 via-blue-600 to-cyan-700 border-0 shadow-2xl relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-white/10 to-transparent"></div>
            <CardContent className="p-12 lg:p-16 relative text-white text-center">
              <h2 className="text-3xl lg:text-4xl font-bold mb-6">
                Fragen zu diesen Themen?
              </h2>
              <p className="text-xl text-cyan-100 mb-10 max-w-2xl mx-auto">
                Ich helfe Ihnen gerne weiter – im kostenlosen Erstgespräch oder bei der Umsetzung.
              </p>
              <div className="flex flex-col sm:flex-row gap-6 justify-center">
                <Button size="lg" asChild className="bg-white text-cyan-700 hover:bg-cyan-50 shadow-xl px-8 py-4 text-lg">
                  <Link to="/contact">
                    <Target className="mr-2 h-5 w-5" />
                    Jetzt Gespräch vereinbaren
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Link>
                </Button>
                <Button size="lg" variant="outline" asChild className="border-2 border-white/30 text-white hover:bg-white/10 px-8 py-4 text-lg">
                  <a href="tel:015679697252">
                    <Phone className="mr-2 h-5 w-5" />
                    0156-79697252
                  </a>
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>
    </div>
  );
};

export default Wissen;
